/**
 * Client-side KeepsakePrayerSourceV1 construction + digest
 * (PC-B rev 9, section 10b.1).
 *
 * Byte-exact mirror of the construction rule the server applies
 * (034's keepsake_prayer_source_canonical): absent `parsed` becomes
 * null; otherwise copy only its own JSON properties recursively and
 * preserve omitted optional nested keys as omitted; absent
 * `progressHistory` becomes []; gardenSummary and testimony use ?? null;
 * isPrivate uses ?? false; all three timestamps pass through the exact
 * ISODate normalizer before hashing; a present legacy local row has
 * deletedAt = null unless an acknowledged S0 soft-delete intent supplies
 * the normalized server value.
 *
 * Values outside the JSON domain (undefined, non-finite numbers,
 * accessors via getters, functions) REJECT (throw) — the caller treats a
 * throw as "digest unavailable" (fail closed, fence stays unmatched).
 */

import type { Prayer } from '../../types/prayer';
import { jcsCanonicalize, sha256Hex } from './canonical-json';
import { normalizeIsoDate } from './isodate';

type JsonValue = null | boolean | number | string | JsonValue[] | { [k: string]: JsonValue };

/** Deep copy of own JSON properties; undefined-valued keys are OMITTED. */
function copyJson(v: unknown): JsonValue {
  if (v === null) return null;
  const t = typeof v;
  if (t === 'string' || t === 'boolean') return v as JsonValue;
  if (t === 'number') {
    if (!Number.isFinite(v as number)) throw new Error('source: non-finite number');
    return v as number;
  }
  if (Array.isArray(v)) {
    return v.map((item) => (item === undefined ? null : copyJson(item)));
  }
  if (t === 'object') {
    const out: { [k: string]: JsonValue } = {};
    for (const k of Object.keys(v as Record<string, unknown>)) {
      const val = (v as Record<string, unknown>)[k];
      if (val === undefined) continue; // omitted optional keys stay omitted
      out[k] = copyJson(val);
    }
    return out;
  }
  throw new Error('source: non-JSON value');
}

function requireIso(v: unknown, field: string): string {
  const iso = normalizeIsoDate(typeof v === 'string' ? v : null);
  if (iso === null) throw new Error(`source: invalid ${field}`);
  return iso;
}

export interface BuildSourceOptions {
  /** Normalized server deletedAt from an acknowledged soft-delete intent. */
  acknowledgedDeletedAt?: string | null;
}

/** The exact canonical bytes of KeepsakePrayerSourceV1 for a local row. */
export function buildPrayerSourceCanonical(
  ownerId: string,
  prayer: Prayer,
  options: BuildSourceOptions = {},
): string {
  const status = prayer.status ?? 'Open';
  if (
    status !== 'Open' &&
    status !== 'In Progress' &&
    status !== 'Answered' &&
    status !== 'Moving'
  ) {
    throw new Error('source: unknown status');
  }
  const body = {
    schema: 'adia-keepsake-prayer-source-v1',
    ownerId,
    id: prayer.id,
    rawText: prayer.rawText,
    parsed: prayer.parsed == null ? null : copyJson(prayer.parsed),
    status,
    progressHistory: copyJson(prayer.progressHistory ?? []),
    gardenSummary: prayer.gardenSummary ?? null,
    testimony: prayer.testimony ?? null,
    isPrivate: prayer.isPrivate ?? false,
    createdAt: requireIso(prayer.createdAt, 'createdAt'),
    updatedAt: requireIso(prayer.updatedAt, 'updatedAt'),
    deletedAt:
      options.acknowledgedDeletedAt !== undefined ? options.acknowledgedDeletedAt : null,
  };
  return jcsCanonicalize(body);
}

/** Lowercase SHA-256 hex over the canonical bytes (WebCrypto, async). */
export async function computePrayerSourceDigest(
  ownerId: string,
  prayer: Prayer,
  options: BuildSourceOptions = {},
): Promise<string> {
  return sha256Hex(buildPrayerSourceCanonical(ownerId, prayer, options));
}
